import { collection, onSnapshot, query, where, orderBy, limit, } from 'firebase/firestore';
/**
 * Client subscription to the durable activity `inbox` (§6.4).
 *
 * Unlike the cache-sync `FirebaseNotificationService` (transient, delete-on-consume),
 * inbox items are DURABLE — they survive reload and are marked seen/read, never
 * deleted on read. This is the read side of the backend `ActivityService` /
 * `WebActivityTransport`, which write the top-level `inbox` collection.
 *
 * The App forwards `onChange` to `AppEventType.ACTIVITY_UPDATED`; components read
 * the current list via `App.getActivityItems()` / `getActivityUnreadCount()`.
 */
export class ActivityInboxService {
    constructor(db, logger) {
        this.db = db;
        this.logger = logger;
        this.unsubscribe = null;
        this.items = [];
        this.listeners = [];
        /** Newest-first cap — matches the backend inbox retention intent (§6.4). */
        this.MAX_ITEMS = 50;
    }
    /** Register a change listener. Returns an unsubscribe function. */
    onChange(listener) {
        this.listeners.push(listener);
        return () => { this.listeners = this.listeners.filter((l) => l !== listener); };
    }
    getItems() {
        return this.items;
    }
    getUnreadCount() {
        return this.items.reduce((n, item) => (item.read ? n : n + 1), 0);
    }
    /** Start the realtime listener for the given authenticated user. */
    start(userId) {
        if (this.unsubscribe)
            return; // already listening
        const q = query(collection(this.db, 'inbox'), where('userId', '==', userId), orderBy('createdAtUtc', 'desc'), limit(this.MAX_ITEMS));
        this.unsubscribe = onSnapshot(q, (snapshot) => {
            this.items = snapshot.docs
                .map((d) => this.parse(d.id, d.data()))
                .filter((item) => item !== null);
            this.notifyListeners();
        }, (error) => {
            this.logger.warn('Activity inbox listener error:', error);
        });
    }
    /** Stop listening and clear local state. */
    stop() {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.items = [];
        this.notifyListeners();
    }
    notifyListeners() {
        this.listeners.forEach((l) => l());
    }
    /** Validate the minimal shape before trusting a Firestore doc. */
    parse(docId, data) {
        if (typeof data.id !== 'string'
            || typeof data.userId !== 'string'
            || typeof data.title !== 'string'
            || typeof data.createdAtUtc !== 'number') {
            this.logger.warn(`Malformed inbox item ${docId} — skipping`);
            return null;
        }
        // Normalize collection/flag fields so the renderer never crashes on a
        // partially-written or schema-drifted doc: `ActivityCard` does
        // `item.actions.filter(...)` and would throw on a missing `actions`.
        return {
            ...data,
            actions: Array.isArray(data.actions) ? data.actions : [],
            body: typeof data.body === 'string' ? data.body : '',
            seen: data.seen === true,
            read: data.read === true,
        };
    }
}
